// The render loop and its stats (P0.8).
//
// Shared by both paths on purpose. The worker and the main-thread fallback run *this* loop, write
// *this* buffer, and differ only in which thread they happen on. That is the difference between a
// fallback and a second implementation: a second implementation drifts, and the path nobody
// develops on is the one that breaks silently.

import { RENDER_STATS } from '../../platform/types/render';
import {
  clearHistogram,
  readDistribution,
  recordSample,
  type FrameDistributionMs,
} from './frame-histogram';
import type { Renderer } from './renderer';

const FRAME_COUNT_I32 = RENDER_STATS.FRAME_COUNT >>> 2;
const LAST_FRAME_I32 = RENDER_STATS.LAST_FRAME_MICROS >>> 2;
const MAX_FRAME_I32 = RENDER_STATS.MAX_FRAME_MICROS >>> 2;
const MAX_INTERVAL_I32 = RENDER_STATS.MAX_INTERVAL_MICROS >>> 2;
const INTERVAL_HISTOGRAM_I32 = RENDER_STATS.INTERVAL_HISTOGRAM >>> 2;
const CPU_HISTOGRAM_I32 = RENDER_STATS.CPU_HISTOGRAM >>> 2;

const MICROS_PER_MS = 1000;

/**
 * Clamp on the delta handed to `frame()`.
 *
 * A tab restored after a minute in the background reports a 60-second delta, and a scene that
 * integrates it teleports. Everything real-time does this; the number is arbitrary but the need
 * is not.
 */
const MAX_DELTA_SECONDS = 0.1;

export class RenderLoop {
  readonly #renderer: Renderer;
  readonly #stats: Int32Array;
  #handle: number | null = null;
  #lastTimestamp: number | null = null;

  constructor(renderer: Renderer, stats: SharedArrayBuffer) {
    this.#renderer = renderer;
    this.#stats = new Int32Array(stats);
  }

  start(): void {
    if (this.#handle !== null) {
      return;
    }
    this.#handle = requestAnimationFrame(this.#tick);
  }

  stop(): void {
    if (this.#handle !== null) {
      cancelAnimationFrame(this.#handle);
      this.#handle = null;
    }
    this.#lastTimestamp = null;
  }

  /**
   * An arrow field, not a method: `requestAnimationFrame` calls it unbound, and a bound method
   * created per frame would allocate on the frame budget.
   */
  readonly #tick = (timestamp: number): void => {
    this.#handle = requestAnimationFrame(this.#tick);

    const previous = this.#lastTimestamp;
    this.#lastTimestamp = timestamp;

    // The first frame has no previous timestamp, so it has no delta. Rendering it with 0 is
    // right: draw the scene where it starts rather than guessing 16 ms of motion.
    const deltaSeconds =
      previous === null ? 0 : Math.min((timestamp - previous) / 1000, MAX_DELTA_SECONDS);

    const started = performance.now();
    this.#renderer.frame(deltaSeconds);
    const micros = Math.round((performance.now() - started) * MICROS_PER_MS);

    Atomics.store(this.#stats, LAST_FRAME_I32, micros);
    if (micros > Atomics.load(this.#stats, MAX_FRAME_I32)) {
      Atomics.store(this.#stats, MAX_FRAME_I32, micros);
    }
    Atomics.add(this.#stats, FRAME_COUNT_I32, 1);

    // The distribution (P0.12). Two `Atomics.add`s — the reason §6.3's controller can have a
    // distribution to apply hysteresis against without anything allocating on the frame budget.
    //
    // CPU is the time just measured: how long *we* took to submit the frame. Interval is how long
    // the frame took to arrive, which is the reciprocal of FPS and therefore the only one NFR-P3
    // is a claim about. Both, because they answer different questions and a bench that could not
    // tell them apart would report a comfortable 2 ms CPU while the user watched 30 FPS — GL calls
    // are asynchronous, so a GPU-bound frame is invisible to a CPU timer.
    recordSample(this.#stats, CPU_HISTOGRAM_I32, micros);

    // Skipped on the first frame: `previous === null` means there is no interval yet, and feeding
    // bucket 0 a frame that never happened would put a fictional 0 ms in every run's p50.
    if (previous !== null) {
      const intervalMicros = Math.round((timestamp - previous) * MICROS_PER_MS);
      recordSample(this.#stats, INTERVAL_HISTOGRAM_I32, intervalMicros);

      // The true worst, beside the bucketed one. The histogram saturates at 63.75 ms, which is
      // precisely where the interesting stalls start.
      if (intervalMicros > Atomics.load(this.#stats, MAX_INTERVAL_I32)) {
        Atomics.store(this.#stats, MAX_INTERVAL_I32, intervalMicros);
      }
    }
  };
}

/** What the UI needs to show a frame-rate readout. */
export interface RenderStatsSnapshot {
  readonly frameCount: number;
  readonly lastFrameMs: number;
  readonly maxFrameMs: number;
}

export function readRenderStats(stats: SharedArrayBuffer): RenderStatsSnapshot {
  const view = new Int32Array(stats);
  return {
    frameCount: Atomics.load(view, FRAME_COUNT_I32),
    lastFrameMs: Atomics.load(view, LAST_FRAME_I32) / MICROS_PER_MS,
    maxFrameMs: Atomics.load(view, MAX_FRAME_I32) / MICROS_PER_MS,
  };
}

export function resetRenderStats(stats: SharedArrayBuffer): void {
  const view = new Int32Array(stats);
  Atomics.store(view, FRAME_COUNT_I32, 0);
  Atomics.store(view, LAST_FRAME_I32, 0);
  Atomics.store(view, MAX_FRAME_I32, 0);
  Atomics.store(view, MAX_INTERVAL_I32, 0);
  clearHistogram(view, INTERVAL_HISTOGRAM_I32);
  clearHistogram(view, CPU_HISTOGRAM_I32);
}

/**
 * Both distributions (P0.12). The bench's frame-time half.
 *
 * Read from the main thread while the renderer keeps writing, and deliberately not synchronised:
 * a percentile computed while a frame lands is a percentile that is one sample stale, which is
 * nothing. Stopping the renderer to read it consistently would mean the bench perturbing the thing
 * it measures.
 */
export interface FrameDistributions {
  /** Frame arrival times. The reciprocal of FPS — what NFR-P3's ≥60 is about. */
  readonly interval: FrameDistributionMs;
  /** Time spent submitting each frame. Small while interval is large means GPU-bound. */
  readonly cpu: FrameDistributionMs;
  /**
   * The longest gap between two frames, unbucketed and exact.
   *
   * `interval.max` saturates at the histogram's last bucket (63.75 ms); this does not. When the two
   * disagree, this one is the truth and `interval.overflow` says how many frames are hiding behind
   * it. Null when fewer than two frames were rendered — there is no interval without a pair.
   */
  readonly maxIntervalMs: number | null;
}

export function readFrameDistributions(stats: SharedArrayBuffer): FrameDistributions {
  const view = new Int32Array(stats);
  const interval = readDistribution(view, INTERVAL_HISTOGRAM_I32);
  const maxIntervalMicros = Atomics.load(view, MAX_INTERVAL_I32);

  return {
    interval,
    cpu: readDistribution(view, CPU_HISTOGRAM_I32),
    // Gated on the histogram rather than on the scalar being non-zero: a genuine 0 µs interval is
    // absurd but a *missing* measurement reading 0 is the bug this repo keeps meeting.
    maxIntervalMs: interval.count === 0 ? null : maxIntervalMicros / MICROS_PER_MS,
  };
}

/** The histogram bases, for callers doing their own percentile maths (the bench's FPS figures). */
export const HISTOGRAM_BASE_I32 = {
  interval: INTERVAL_HISTOGRAM_I32,
  cpu: CPU_HISTOGRAM_I32,
} as const;
